import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Button from './Button/Button';
import ChevronDownIcon from './Icons/ChevronDownIcon';
import CheckIcon from './Icons/CheckIcon';
import { theme } from '../theme';
import Typography from './Typography';
import FilterSolidIcon from './Icons/FilterSolidIcon';

const DropdownContainer = styled.div`
  display: inline-block;
  position: relative;
`;

const DropdownButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.xs};
  min-width: ${({ $minWidth }) => $minWidth || "160px"};
`;

const DropdownIconWrapper = styled.div`
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div`
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  display: flex;
  flex-direction: column;
  margin: 4px 0 0;
  min-width: 200px;
  overflow: hidden;
  position: absolute;
  left: 0;
  top: 100%;
  z-index: 1100;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.xs};
`;

const DropdownMenuItem = styled.div`
  align-items: center;
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.xs};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const MenuItemContent = styled(Typography)`
  flex: 1;
`;

const CheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;
  opacity: ${({ $isSelected }) => $isSelected ? 1 : 0};
`;

const DropdownContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const FilterDropdown = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
  multiSelect = true,
  placeholder = "Seleccionar...",
  showIcon = true,
  customButtonContent = null,
  minWidth = "160px",
  variant = "outline"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const handleOptionClick = (optionValue) => {
    if (multiSelect) {
      const newSelection = selectedValues.includes(optionValue)
        ? selectedValues.filter(val => val !== optionValue)
        : [...selectedValues, optionValue];
      onSelectionChange(newSelection);
    } else {
      onSelectionChange([optionValue]);
      setIsOpen(false);
    }
  };

  const getButtonLabel = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }

    if (multiSelect) {
      if (selectedValues.length === options.length) {
        return placeholder; // Mostrar placeholder cuando todos están seleccionados
      }
      if (selectedValues.length === 1) {
        const selectedOption = options.find(opt => opt.value === selectedValues[0]);
        return selectedOption ? selectedOption.label : placeholder;
      }
      return `${selectedValues.length} seleccionados`;
    } else {
      const selectedOption = options.find(opt => opt.value === selectedValues[0]);
      return selectedOption ? selectedOption.label : placeholder;
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton
        $variant={variant}
        size="l"
        sizeTablet="l"
        sizeMobile="m"
        $isOpen={isOpen}
        $minWidth={minWidth}
        onClick={toggleDropdown}
      >
        {customButtonContent ? (
          customButtonContent
        ) : (
          <>
            <DropdownContent>
              {showIcon && <FilterSolidIcon fill={theme.colors.defaultStrong} width={20} height={20} />}
              <Typography $variant="body-2-semibold" color={theme.colors.defaultStrong}>
                {getButtonLabel()}
              </Typography>
            </DropdownContent>
            <DropdownIconWrapper $isOpen={isOpen}>
              <ChevronDownIcon
                fill={theme.colors.defaultStrong}
                width={20}
                height={20}
              />
            </DropdownIconWrapper>
          </>
        )}
      </DropdownButton>

      {isOpen && (
        <DropdownMenu>
          <InnerWrapper>
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                >
                  <MenuItemContent $variant="body-2-medium">
                    {option.label}
                  </MenuItemContent>
                  <CheckIconWrapper $isSelected={isSelected}>
                    <CheckIcon
                      fill={theme.colors.brandMain}
                      width={16}
                      height={16}
                    />
                  </CheckIconWrapper>
                </DropdownMenuItem>
              );
            })}
          </InnerWrapper>
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

FilterDropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedValues: PropTypes.array.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  multiSelect: PropTypes.bool,
  placeholder: PropTypes.string,
  showIcon: PropTypes.bool,
  customButtonContent: PropTypes.node,
  minWidth: PropTypes.string,
  variant: PropTypes.string,
};

export default FilterDropdown;